/* [EZ] Carousel — reusable translate-track slider.
   Markup contract:
     [data-ez-carousel][data-per-view][data-step][data-autoplay][data-loop]
       [data-ez-carousel-viewport]
         [data-ez-carousel-track]  (direct children = cells)
       [data-ez-carousel-prev]   (optional)
       [data-ez-carousel-next]   (optional)
   per-view is computed from the cells' actual width (responsive); data-per-view
   is a fallback for when cells are measured at 0 width (e.g. a hidden panel).
   data-step = cells advanced per arrow. data-autoplay="<seconds>" auto-advances;
   data-loop wraps at the ends. Autoplay pauses on hover and respects reduced motion.
   Reused by: [EZ] Header mega panels, [EZ] Awards slider. */
(function () {
  'use strict';

  class EZCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-ez-carousel-viewport]');
      this.track = root.querySelector('[data-ez-carousel-track]');
      this.prev = root.querySelector('[data-ez-carousel-prev]');
      this.next = root.querySelector('[data-ez-carousel-next]');
      this.progress = root.querySelector('[data-ez-carousel-progress]');
      if (!this.track) return;
      this.cells = Array.from(this.track.children);
      this.fallbackPerView = parseInt(root.dataset.perView, 10) || 1;
      this.perView = this.fallbackPerView;
      this.step = parseInt(root.dataset.step, 10) || 1;
      this.autoplayMs = (parseFloat(root.dataset.autoplay) || 0) * 1000;
      this.loop = root.hasAttribute('data-loop') || this.autoplayMs > 0;
      this.index = 0;

      if (this.prev) this.prev.addEventListener('click', () => this.go(this.index - this.step));
      if (this.next) this.next.addEventListener('click', () => this.go(this.index + this.step));
      window.addEventListener('resize', this.debounce(() => this.render(), 150));
      if (root.hasAttribute('data-draggable')) this.enableDrag();
      this.render();

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.autoplayMs && !reduce) {
        this.start();
        root.addEventListener('mouseenter', () => this.stop());
        root.addEventListener('mouseleave', () => this.start());
        root.addEventListener('focusin', () => this.stop());
        root.addEventListener('focusout', () => this.start());
      }
    }

    metrics() {
      const cw = this.cells.length ? this.cells[0].getBoundingClientRect().width : 0;
      const vw = (this.viewport || this.track).getBoundingClientRect().width;
      this.perView = cw > 0 ? Math.max(1, Math.round(vw / cw)) : this.fallbackPerView;
      this.maxIndex = Math.max(0, this.cells.length - this.perView);
      return cw;
    }

    /* Touch/pen drag (opt-in via data-draggable). Horizontal swipe pages the track;
       vertical scroll is left to the browser (touch-action: pan-y in CSS). A real drag
       suppresses the click so cards/links don't fire on release.
       Mouse is intentionally excluded: dragging is a mobile-only affordance, and
       capturing the pointer on a mouse click was swallowing card/link clicks on
       desktop. Desktop navigates via the cards and pages via the arrows. */
    enableDrag() {
      const vp = this.viewport || this.track;
      const self = this;
      let dragging = false, startX = 0, startT = 0, moved = 0, cw = 0;

      vp.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse') return;
        dragging = true; moved = 0; self.justDragged = false;
        startX = e.clientX;
        cw = self.metrics();
        startT = -(self.index * cw);
        self.track.style.transition = 'none';
        self.stop();
        if (vp.setPointerCapture) { try { vp.setPointerCapture(e.pointerId); } catch (err) {} }
      });
      vp.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        moved = e.clientX - startX;
        self.track.style.transform = 'translateX(' + (startT + moved) + 'px)';
      });
      function end() {
        if (!dragging) return;
        dragging = false;
        self.track.style.transition = '';
        if (Math.abs(moved) > 5) self.justDragged = true;
        if (cw > 0 && Math.abs(moved) > cw * 0.2) self.go(self.index - Math.round(moved / cw));
        else self.render();
        self.start();
      }
      vp.addEventListener('pointerup', end);
      vp.addEventListener('pointercancel', end);
      vp.addEventListener('dragstart', function (e) { e.preventDefault(); });
      vp.addEventListener('click', function (e) {
        if (self.justDragged) { e.preventDefault(); e.stopPropagation(); self.justDragged = false; }
      }, true);
    }

    go(target) {
      if (this.loop) {
        if (target > this.maxIndex) target = 0;
        else if (target < 0) target = this.maxIndex;
      } else {
        target = Math.max(0, Math.min(target, this.maxIndex));
      }
      this.index = target;
      this.render();
    }

    render() {
      if (!this.cells.length) return;
      const cellWidth = this.metrics();
      if (this.index > this.maxIndex) this.index = this.maxIndex;
      this.track.style.transform = 'translateX(' + -(this.index * cellWidth) + 'px)';

      const fits = this.cells.length <= this.perView;
      // When every cell fits within per-view, let the section hide its pager entirely
      // (opt-in via CSS on .ez-carousel-fits). Responsive: re-evaluated on resize.
      this.root.classList.toggle('ez-carousel-fits', fits);
      this.setArrow(this.prev, !fits && this.index > 0);
      this.setArrow(this.next, !fits && this.index < this.maxIndex);

      // Reveal-progress fill: how much of the list is shown (visible ÷ total → 100% at the end)
      if (this.progress) {
        const shown = Math.min(this.cells.length, this.index + this.perView);
        const pct = this.cells.length ? (shown / this.cells.length) * 100 : 100;
        this.progress.style.width = pct + '%';
      }
    }

    start() {
      if (!this.autoplayMs || this.timer) return;
      if (this.cells.length <= this.perView) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.autoplayMs);
    }

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    setArrow(btn, available) {
      if (!btn) return;
      btn.classList.toggle('is-available', available);
      btn.setAttribute('aria-hidden', String(!available));
      btn.tabIndex = available ? 0 : -1;
    }

    debounce(fn, wait) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-ez-carousel]').forEach(function (el) {
      if (el.__ezCarousel) return;
      el.__ezCarousel = new EZCarousel(el);
    });
  }

  window.EZCarousel = EZCarousel;
  window.ezCarouselInit = init;

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
